// Unified Action Center — one operational inbox pulling from the
// Executive Reasoning Engine (recommendations), Waiting On Others
// (waiting_items), Waiting On Me (waiting_on_me), and Pending Proposals
// (assistant_pending_updates). Everything routes back to the same engine
// APIs used by the Brief and Assistant.
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import {
  PRIORITY_ORDER,
  priorityBadgeClass,
  Priority,
  useExecutiveSnapshot,
  useInvalidateSnapshot,
} from "@/lib/executive/engine";
import { RecommendationCard } from "@/components/executive/RecommendationCard";

interface WaitingOnMeRow {
  id: string;
  title: string;
  description: string | null;
  priority: Priority;
  due_date: string | null;
  status: string;
  source: string;
  linked_records: any;
  created_at: string;
}

export default function ActionCenter() {
  const { data, isLoading, refetch, isFetching } = useExecutiveSnapshot();
  const invalidate = useInvalidateSnapshot();
  const [wom, setWom] = useState<WaitingOnMeRow[]>([]);
  const [womLoading, setWomLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("medium");
  const [creating, setCreating] = useState(false);

  const loadWom = async () => {
    setWomLoading(true);
    const { data, error } = await supabase
      .from("waiting_on_me" as any)
      .select("*")
      .eq("status", "open")
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Couldn't pull today's action list", description: "Try refreshing in a moment.", variant: "destructive" });
    else setWom((data ?? []) as unknown as WaitingOnMeRow[]);
    setWomLoading(false);
  };

  useEffect(() => {
    loadWom();
  }, []);

  const addWom = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    const { error } = await supabase.from("waiting_on_me" as any).insert({
      title,
      priority: newPriority,
      source: "manual",
    } as any);
    setCreating(false);
    if (error) {
      friendlyError({ what: "I couldn't add that to your action list.", technical: error });
      return;
    }
    setNewTitle("");
    loadWom();
  };

  const completeWom = async (id: string) => {
    const { error } = await supabase
      .from("waiting_on_me" as any)
      .update({ status: "completed", completed_at: new Date().toISOString() } as any)
      .eq("id", id);
    if (error) friendlyError({ what: "I couldn't mark that as done.", technical: error });
    else loadWom();
  };

  const recs = useMemo(
    () =>
      (data?.recommendations ?? [])
        .slice()
        .sort((a, b) => (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) || b.score - a.score),
    [data],
  );

  return (
    <AdminShell>
      <div className="space-y-6 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Action Center</h1>
            <p className="text-sm text-muted-foreground">
              Everything that needs a decision, ordered by priority.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { refetch(); invalidate(); loadWom(); }} disabled={isFetching}>
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
        </div>

        <Tabs defaultValue="recommendations">
          <TabsList>
            <TabsTrigger value="recommendations">
              Recommendations
              <Badge variant="secondary" className="ml-2">{recs.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="waiting_me">
              Waiting on Me
              <Badge variant="secondary" className="ml-2">{wom.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="waiting_others">
              Waiting on Others
              <Badge variant="secondary" className="ml-2">
                {data?.brief.counts.waiting_others ?? 0}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recommendations" className="space-y-2 mt-4">
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading recommendations…
              </div>
            )}
            {!isLoading && recs.length === 0 && (
              <div className="text-sm text-muted-foreground">Nothing to review. Nice.</div>
            )}
            {recs.map((r) => (
              <RecommendationCard key={r.id} rec={r} />
            ))}
          </TabsContent>

          <TabsContent value="waiting_me" className="space-y-3 mt-4">
            <Card>
              <CardContent className="p-3 flex items-center gap-2">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="What did you commit to? (e.g. Email Ashley about supplies)"
                  onKeyDown={(e) => e.key === "Enter" && addWom()}
                />
                <select
                  className="border rounded px-2 py-1 text-sm bg-background"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as Priority)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <Button size="sm" onClick={addWom} disabled={creating || !newTitle.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardContent>
            </Card>
            {womLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            )}
            {!womLoading && wom.length === 0 && (
              <div className="text-sm text-muted-foreground">Nothing on your plate.</div>
            )}
            {wom.map((w) => (
              <Card key={w.id} className="p-3">
                <div className="flex items-center gap-3">
                  <Badge className={priorityBadgeClass(w.priority)}>{w.priority.toUpperCase()}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{w.title}</div>
                    {w.description && (
                      <div className="text-xs text-muted-foreground">{w.description}</div>
                    )}
                    {w.due_date && (
                      <div className="text-xs text-muted-foreground">
                        Due {new Date(w.due_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => completeWom(w.id)}>
                    Mark done
                  </Button>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="waiting_others" className="mt-4">
            <p className="text-sm text-muted-foreground">
              Managed in the Waiting page — this tab summarizes the count. Open the{" "}
              <a href="/admin/waiting" className="underline">Waiting page</a> to add or update items.
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}
